// src/services/aiAssistantService.ts
//
// Service frontend pour l'assistant IA conversationnel SAGeCom (v1).
// Consomme les endpoints `/ai/*` du backend (synchrone, non-streaming).
//
// Mai 2026.
import axiosInstance from './axios.config';

const BASE_URL = '/ai';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AiMessageRole = 'user' | 'assistant';

export interface AiMessage {
  id: string;
  conversationId: string;
  role: AiMessageRole;
  contenu: string;
  tokensInput?: number;
  tokensOutput?: number;
  contextePage?: string | null;
  modeleUtilise?: string | null;
  erreur?: string | null;
  createdAt: string;
}

export interface AiConversation {
  id: string;
  userId: string;
  entiteId: string;
  titre: string;
  modele?: string;
  nbMessages: number;
  totalTokensInput: number;
  totalTokensOutput: number;
  contextePage?: string | null;
  contexteModule?: string | null;
  archivee: boolean;
  createdAt: string;
  updatedAt: string;
  messages?: AiMessage[];
}

export interface ListConversationsOptions {
  archivees?: boolean;
  limit?: number;
}

export interface CreateConversationPayload {
  titre?: string;
  contextePage?: string | null;
  contexteModule?: string | null;
  employeId?: string | null;
}

export interface SendMessagePayload {
  contenu: string;
  contextePage?: string | null;
  contexteModule?: string | null;
  employeId?: string | null;
}

export interface SendMessageResponse {
  messageUser: AiMessage;
  messageAssistant: AiMessage;
  conversation: AiConversation;
}

export interface UpdateConversationPayload {
  titre?: string;
  archivee?: boolean;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const aiAssistantService = {
  listConversations: async (opts?: ListConversationsOptions): Promise<AiConversation[]> => {
    const params: Record<string, any> = {};
    if (opts?.archivees !== undefined) params.archivees = opts.archivees;
    if (opts?.limit !== undefined) params.limit = opts.limit;
    const response = await axiosInstance.get(`${BASE_URL}/conversations`, { params });
    return response.data;
  },

  getConversation: async (id: string): Promise<AiConversation> => {
    const response = await axiosInstance.get(`${BASE_URL}/conversations/${id}`);
    return response.data;
  },

  createConversation: async (
    payload: CreateConversationPayload,
  ): Promise<AiConversation> => {
    const response = await axiosInstance.post(`${BASE_URL}/conversations`, payload);
    return response.data;
  },

  sendMessage: async (
    conversationId: string,
    payload: SendMessagePayload,
  ): Promise<SendMessageResponse> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/conversations/${conversationId}/messages`,
      payload,
    );
    return response.data;
  },

  updateConversation: async (
    id: string,
    payload: UpdateConversationPayload,
  ): Promise<AiConversation> => {
    const response = await axiosInstance.patch(`${BASE_URL}/conversations/${id}`, payload);
    return response.data;
  },

  deleteConversation: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/conversations/${id}`);
  },

  clearAllConversations: async (): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/conversations/clear-all`);
  },
};

export default aiAssistantService;
